import React, { useEffect, useRef, useState, useCallback } from "react";
import { Headset, Send, Mail, Bot, User as UserIcon, Circle } from "lucide-react";
import client from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";

const WS_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");

const Support = () => {
  const [online, setOnline] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [contacts, setContacts] = useState([]);
  const wsRef = useRef(null);
  const endRef = useRef(null);

  const loadSessions = useCallback(() => client.get("/admin/chat/sessions").then((r) => setSessions(r.data.items)), []);
  const loadContacts = useCallback(() => client.get("/admin/contact").then((r) => setContacts(r.data.items)), []);

  useEffect(() => {
    client.get("/admin/agent-online").then((r) => setOnline(r.data.online));
    loadSessions(); loadContacts();
    const token = localStorage.getItem("gp_token");
    try {
      const ws = new WebSocket(`${WS_BASE}/api/ws/agent?token=${token}`);
      ws.onmessage = (e) => {
        try {
          const d = JSON.parse(e.data);
          if (d.type === "message") { loadSessions(); setActive((a) => { if (a && d.session_id === a) setMessages((m) => (m.some((x) => x.id === d.id) ? m : [...m, d])); return a; }); }
          if (d.type === "session_update") loadSessions();
        } catch {}
      };
      wsRef.current = ws;
    } catch {}
    const t = setInterval(loadSessions, 5000);
    return () => { clearInterval(t); wsRef.current && wsRef.current.close(); };
  }, [loadSessions, loadContacts]);

  useEffect(() => { endRef.current?.scrollIntoView(); }, [messages]);

  const openSession = async (sid) => {
    setActive(sid);
    const { data } = await client.get(`/admin/chat/sessions/${sid}`);
    setMessages(data.messages);
    loadSessions();
  };

  const toggleOnline = async (v) => { setOnline(v); await client.post("/admin/agent-online", { online: v }); };

  const send = async () => {
    const t = text.trim();
    if (!t || !active) return;
    setText("");
    const { data } = await client.post(`/admin/chat/sessions/${active}/message`, { text: t });
    setMessages((m) => [...m, data]);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div><h1 className="text-2xl font-bold tracking-tight text-graphite">Support</h1><p className="text-sm text-steel-light mt-1">Live chat, chatbot fallback & contact messages.</p></div>
        <div className="flex items-center gap-3 bg-white border border-border rounded-md px-4 h-11">
          <span className={`w-2.5 h-2.5 rounded-full ${online ? "bg-emerald-500" : "bg-steel-light"}`} />
          <span className="text-sm font-medium text-graphite">I'm online</span>
          <Switch data-testid="agent-online-toggle" checked={online} onCheckedChange={toggleOnline} />
        </div>
      </div>

      <Tabs defaultValue="chats">
        <TabsList>
          <TabsTrigger value="chats" data-testid="tab-chats"><Headset className="w-4 h-4 mr-1.5" /> Live Chats</TabsTrigger>
          <TabsTrigger value="contacts" data-testid="tab-contacts"><Mail className="w-4 h-4 mr-1.5" /> Contact Messages</TabsTrigger>
        </TabsList>

        <TabsContent value="chats" className="mt-4">
          <div className="grid lg:grid-cols-3 gap-4 h-[60vh]">
            <div className="border border-border rounded-lg overflow-y-auto thin-scroll bg-white">
              {sessions.length === 0 && <div className="p-6 text-center text-sm text-steel-light">No conversations yet.</div>}
              {sessions.map((s) => (
                <button key={s.id} data-testid={`chat-session-${s.id}`} onClick={() => openSession(s.id)} className={`w-full text-left p-3 border-b border-border ${active === s.id ? "bg-orange/5" : "hover:bg-surface/50"}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-graphite text-sm truncate">{s.name || s.email}</span>
                    {s.unread_agent > 0 && <span className="bg-orange text-white text-[10px] mono rounded-full px-1.5 py-0.5">{s.unread_agent}</span>}
                  </div>
                  <div className="text-xs text-steel-light truncate">{s.email}</div>
                  <div className="text-xs text-steel-light truncate mt-0.5">{s.last_sender === "bot" ? "🤖 " : s.last_sender === "agent" ? "↩ " : ""}{s.last_message}</div>
                </button>
              ))}
            </div>

            <div className="lg:col-span-2 border border-border rounded-lg flex flex-col bg-white">
              {!active ? <div className="flex-1 grid place-items-center text-steel-light text-sm">Select a conversation</div> : (
                <>
                  <div className="flex-1 overflow-y-auto thin-scroll p-3 space-y-2 bg-surface/30">
                    {messages.map((m) => {
                      const right = m.sender === "agent";
                      const Icon = m.sender === "customer" ? UserIcon : m.sender === "bot" ? Bot : Headset;
                      return (
                        <div key={m.id} className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${right ? "bg-graphite text-white ml-auto" : m.sender === "bot" ? "bg-white border border-border text-graphite" : "bg-orange/10 text-graphite"}`}>
                          <div className="text-[10px] uppercase tracking-wider opacity-60 mb-0.5 flex items-center gap-1"><Icon className="w-3 h-3" /> {m.sender}</div>
                          <div className="whitespace-pre-wrap">{m.text}</div>
                        </div>
                      );
                    })}
                    <div ref={endRef} />
                  </div>
                  <div className="p-2.5 border-t border-border flex gap-2">
                    <input data-testid="agent-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Reply as agent…" className="flex-1 h-10 px-3 border border-border rounded-md outline-none focus:border-orange text-sm" />
                    <button data-testid="agent-send" onClick={send} className="w-10 h-10 grid place-items-center bg-orange text-white rounded-md"><Send className="w-4 h-4" /></button>
                  </div>
                </>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="contacts" className="mt-4 space-y-2">
          {contacts.length === 0 && <div className="border border-border rounded-lg py-12 text-center text-steel-light">No contact messages.</div>}
          {contacts.map((c) => (
            <div key={c.id} className="border border-border rounded-lg p-4 bg-white">
              <div className="flex items-center justify-between"><span className="font-semibold text-graphite">{c.name} <span className="text-steel-light font-normal">· {c.email}</span></span><span className="mono text-xs text-steel-light">{new Date(c.created_at).toLocaleString()}</span></div>
              {c.subject && <div className="text-sm font-medium text-steel mt-1">{c.subject}</div>}
              <p className="text-sm text-steel-light mt-1">{c.message}</p>
            </div>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Support;
